import { Components } from 'formiojs'

import { computeNextIncrementNumber, hasMissingIncrementNumber } from './directory-increment-number.util'

const Textfield = Components.components.textfield

export class DirectoryIncrementNumberComponent extends Textfield {
  static schema(...extend) {
    return Textfield.schema(
      {
        type: 'incrementNumber',
        label: 'Fortlaufende Nummer',
        key: 'incrementNumber',
        disabled: true,
        prefix: '',
        verzeichnis: '',
        needsToken: true
      },
      ...extend
    )
  }

  static get builderInfo() {
    return {
      title: 'Fortlaufende Nummer',
      icon: 'hashtag',
      group: 'custom',
      weight: 10,
      schema: DirectoryIncrementNumberComponent.schema()
    }
  }

  static editForm(...extend) {
    const form = Textfield.editForm(...extend)

    let prefixFound = false

    const deduplicatePrefix = (components: any[]): any[] => {
      return components
        .filter((comp) => {
          if (comp.key === 'prefix') {
            if (prefixFound) {
              return false
            }
            prefixFound = true
          }

          return true
        })
        .map((comp) => {
          if (comp.components) {
            comp.components = deduplicatePrefix(comp.components)
          }

          if (comp.columns) {
            comp.columns = comp.columns.map((col: any) => ({
              ...col,
              components: deduplicatePrefix(col.components || [])
            }))
          }

          return comp
        })
    }

    form.components = deduplicatePrefix(form.components)

    return form
  }

  constructor(component, options, data) {
    super(component, options, data)
  }

  /**
   * Hook into the Formio submission process.
   * If the field is empty when saving, generate the number automatically.
   */
  beforeSubmit() {
    if (!this.dataValue && !this.options.builder) {
      console.log('[METRAS-INCREMENT] Auto-generating ID on save...')

      return this.generateNumber()
    }

    return super.beforeSubmit()
  }

  render(content) {
    const container = document.createElement('div')
    const generateButton = document.createElement('button')

    generateButton.setAttribute('ref', `${this.id}-generate`)
    generateButton.type = 'button'
    generateButton.className = 'btn btn-secondary'
    generateButton.style.whiteSpace = 'nowrap'
    generateButton.style.borderTopLeftRadius = '0'
    generateButton.style.borderBottomLeftRadius = '0'
    generateButton.style.marginLeft = '-1px'
    generateButton.innerHTML = '<i class="fa fa-refresh"></i> Generieren'

    container.innerHTML = super.render(content)

    const input = container.querySelector('input')
    const group = container.querySelector('.input-group') || input?.parentElement

    if (input && group) {
      group.classList.add('input-group')
      group.style.display = 'flex'
      group.style.alignItems = 'stretch'
      group.style.width = '100%'

      input.style.flex = '1'

      if (group.children.length > 0) {
        input.style.borderTopRightRadius = '0'
        input.style.borderBottomRightRadius = '0'
      }

      group.appendChild(generateButton)
    }

    return container.innerHTML
  }

  attach(element: any): any {
    this.loadRefs(element, {
      [`${this.id}-generate`]: 'single'
    })

    this.addEventListener(this.refs[`${this.id}-generate`], 'click', async () => {
      console.log('[METRAS-INCREMENT] Manual generation triggered...')
      await this.generateNumber()
    })

    return super.attach(element)
  }

  getAuthHeaders() {
    let token = ''
    let standard = ''

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)

      if (key && key.includes('auth0spajs') && !key.includes('@@user@@')) {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}')

          token = data.body?.access_token || data.access_token || ''

          if (token) {
            break
          }
        } catch (e) {}
      }
    }
    standard = localStorage.getItem('selectedStandard') || ''

    return { Authorization: `Bearer ${token}`, 'x-standard-name': standard || 'default' }
  }

  async generateNumber() {
    if (!this.component.verzeichnis) {
      console.warn('[METRAS-INCREMENT] No directory configured.')

      return
    }

    const prefix = this.component.prefix || 'ID'
    const verzeichnisId = typeof this.component.verzeichnis === 'object' ? this.component.verzeichnis.id : this.component.verzeichnis

    try {
      const url = new URL(`/api-workspace/v1/directories/${verzeichnisId}/data?fields=id,name,formData&removed=true`, import.meta.env.VITE_API_ROOT).toString()
      const headers = this.getAuthHeaders()
      const response = await fetch(url, { headers })

      if (response.status === 401) {
        throw new Error('Nicht autorisiert.')
      }

      let data = await response.json()

      if (Array.isArray(data) && hasMissingIncrementNumber(data, this.component.key, prefix)) {
        try {
          const backfillUrl = new URL(`/api-workspace/v1/directories/${verzeichnisId}/data/backfill-increment-number`, import.meta.env.VITE_API_ROOT).toString()
          const backfillResponse = await fetch(backfillUrl, {
            method: 'PATCH',
            headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ key: this.component.key, prefix })
          })

          if (backfillResponse.ok) {
            data = await backfillResponse.json()
          } else {
            console.warn('[METRAS-INCREMENT] Backfill request failed, continuing without it:', backfillResponse.status)
          }
        } catch (backfillError) {
          console.warn('[METRAS-INCREMENT] Backfill failed, continuing without it:', backfillError)
        }
      }

      const nextId = computeNextIncrementNumber(Array.isArray(data) ? data : [], this.component.key, prefix)

      this.setValue(nextId)

      return nextId
    } catch (error) {
      console.error('[METRAS-INCREMENT] Error:', error)

      return null
    }
  }
}
